"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { toast } from "@/utils/customToast";
import { AvailableRoles } from "@/types";
import { selectAccount } from "@/features/auth/api/onboarding";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler from "@/features/auth/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import useUserStore from "@/features/auth/store/useUserStore.ts";

export interface AccountTypeFormProps {
  goToNextStep: () => void;
}

const accountTypeFormSchema = z.object({
  accountType: z.nativeEnum(AvailableRoles),
});

type AccountTypeFormValues = z.infer<typeof accountTypeFormSchema>;

const availableRoles: AvailableRoles[] = [AvailableRoles.TUTOR, AvailableRoles.STUDENT];

const roleIconMappings = {
  [AvailableRoles.TUTOR]: "tabler:school",
  [AvailableRoles.STUDENT]: "tabler:backpack",
};

const roleColors = {
  [AvailableRoles.TUTOR]: {
    gradient: "from-purple-500/10 to-indigo-500/10",
    iconBg: "from-purple-500/20 to-indigo-500/20",
    iconColor: "text-purple-600 dark:text-purple-400",
  },
  [AvailableRoles.STUDENT]: {
    gradient: "from-blue-500/10 to-cyan-500/10",
    iconBg: "from-blue-500/20 to-cyan-500/20",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
};

const AccountTypeForm = ({ goToNextStep }: AccountTypeFormProps) => {
  const { t } = useTranslation(["forms", "common", "auth"]);

  const { user } = useUserStore();
  const {
    onboardingData: { accountType },
  } = useAuthStore();
  const setOnboardingData = useAuthStore((state) => state.setOnboardingData);
  const form = useForm<AccountTypeFormValues>({
    resolver: zodResolver(accountTypeFormSchema),
    mode: "all",
    defaultValues: {
      accountType: accountType ?? user?.role ?? undefined,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = form;

  const onSubmit: SubmitHandler<AccountTypeFormValues> = async ({ accountType }) => {
    try {
      console.log(accountType);
      await selectAccount(accountType);

      setOnboardingData({ accountType });
      goToNextStep();
    } catch (error) {
      const apiErrors = errorHandler<"account_type">(error);

      if (apiErrors?.account_type) {
        apiErrors.account_type.forEach((error) => {
          toast.error(error);
        });
      }
    }
  };

  return (
    <div>
      <Form {...form}>
        <form className="grid h-full gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <p className="text-accent-foreground text-sm font-medium">
              {t("forms:labels.accountType", "Account Type")}
              <span className="text-destructive ml-1">*</span>
            </p>
          </div>
          <FormField
            control={control}
            name="accountType"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormControl>
                  <RadioGroup
                    className="gap-4"
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                  >
                    {availableRoles.map((role) => (
                      <label
                        key={role}
                        className={cn(
                          "bg-muted/20 border-border/50 hover:bg-muted/30 relative flex w-full cursor-pointer items-start gap-2 rounded-2xl border shadow-xl backdrop-blur-sm transition-all duration-200",
                          "has-[:checked]:border-accent-foreground/30 has-[:checked]:shadow-2xl",
                          "bg-linear-to-tr p-4",
                          roleColors[role].gradient,
                        )}
                        htmlFor={role}
                      >
                        <RadioGroupItem className="sr-only" id={role} value={role} />
                        <div className="flex grow items-center gap-3">
                          <div
                            className={cn(
                              "flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg backdrop-blur-sm",
                              "bg-linear-to-br",
                              roleColors[role].iconBg,
                            )}
                          >
                            <Icon
                              className={cn("text-3xl", roleColors[role].iconColor)}
                              height="30"
                              icon={roleIconMappings[role]}
                              width="30"
                            />
                          </div>
                          <div className="grid grow gap-1">
                            <span className="text-lg font-medium capitalize">
                              {t(`common:roles.${role.toLowerCase()}`, role)}
                            </span>
                            <p className="text-muted-foreground text-sm">
                              {role === AvailableRoles.TUTOR
                                ? t(
                                    "auth:roleDescriptions.tutor",
                                    "Share your knowledge and expertise with students around the world",
                                  )
                                : t(
                                    "auth:roleDescriptions.student",
                                    "Learn from experienced tutors and achieve your educational goals",
                                  )}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </FormControl>
                {errors.accountType && (
                  <p className="text-small text-danger mt-2">{errors.accountType.message}</p>
                )}
              </FormItem>
            )}
          />
          <div className={"relative z-50 w-full"}>
            <Button
              className="bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full shadow-xl"
              disabled={isSubmitting || !form.formState.isValid}
              size="lg"
              type="submit"
            >
              {t("common:continue")}
              <Icon icon={"mdi:arrow-right"} />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AccountTypeForm;
